import Fuse from 'fuse.js';
import { TOOLS } from '@/registry';
import type { Tool } from '@/registry/types';

let fuse: Fuse<Tool> | null = null;

function getFuse(): Fuse<Tool> {
  if (!fuse) {
    fuse = new Fuse<Tool>(TOOLS, {
      keys: [
        { name: 'name', weight: 0.45 },
        { name: 'keywords', weight: 0.35 },
        { name: 'description', weight: 0.15 },
        { name: 'category', weight: 0.05 },
      ],
      threshold: 0.4,
      ignoreLocation: true,
      includeScore: true,
      minMatchCharLength: 2,
    });
  }
  return fuse;
}

/** Search tools by free text. Returns up to `limit` matches. */
export function searchTools(query: string, limit = 8): Tool[] {
  const q = query.trim();
  if (!q) return TOOLS.filter((t) => t.featured).slice(0, limit);
  return getFuse().search(q).slice(0, limit).map((r) => r.item);
}

/* -------------------------------------------------------------------------- */
/*  Natural language → tool intent                                            */
/*                                                                            */
/*  Translates user phrases like "merge these PDFs" / "remove duplicates from */
/*  csv" / "generate a QR code" into a target tool id and a friendly answer.  */
/* -------------------------------------------------------------------------- */

interface IntentRule {
  test: RegExp;
  toolId: string;
  reason: string;
}

const INTENT_RULES: IntentRule[] = [
  // Documents
  { test: /\b(merge|combine|join|concat).*(pdf|document)/i, toolId: 'pdf-merge', reason: 'Merging PDF files into one' },
  { test: /\b(split|break|extract).*(pdf|page)/i, toolId: 'pdf-split', reason: 'Splitting a PDF into pages' },
  { test: /\b(rotate|flip).*(pdf|page)/i, toolId: 'pdf-rotate', reason: 'Rotating PDF pages' },
  { test: /\b(reorder|rearrange).*pdf/i, toolId: 'pdf-rearrange', reason: 'Rearranging PDF pages' },
  { test: /\b(compress|shrink|reduce).*pdf/i, toolId: 'pdf-compress', reason: 'Compressing PDF size' },
  { test: /\b(pdf).*(image|jpg|png)/i, toolId: 'pdf-to-images', reason: 'Converting PDF to images' },
  { test: /\b(image|jpg|png|photo).*(pdf)/i, toolId: 'images-to-pdf', reason: 'Building a PDF from images' },
  { test: /\b(redact|hide|black\s*out)/i, toolId: 'pdf-redact', reason: 'Redacting sensitive content' },
  { test: /\b(sign|signature).*(pdf|document)/i, toolId: 'pdf-signer', reason: 'Sign a PDF' },
  { test: /\b(unlock|remove password|decrypt).*(pdf)/i, toolId: 'pdf-unlock', reason: 'Unlocking PDF' },
  { test: /\b(thumbnail|visual).*(pdf|page)/i, toolId: 'pdf-thumbnails', reason: 'Visual page reorder' },
  { test: /\bmarkdown.*(pdf)/i, toolId: 'md-to-pdf', reason: 'Markdown to PDF' },
  { test: /\bpdf.*(word|docx|doc|editable)/i, toolId: 'pdf-to-word', reason: 'PDF to Word' },
  { test: /\b(template|mail.?merge|fill).*pdf/i, toolId: 'template-pdf', reason: 'Fill template PDF' },
  { test: /\b(placeholder|merge.field).*pdf/i, toolId: 'template-pdf', reason: 'Fill template placeholders' },
  { test: /\b(text|search).*(pdf)/i, toolId: 'pdf-text-extract', reason: 'Extract PDF text' },

  // Images
  { test: /\b(resize|scale).*(image|photo|jpg|png)/i, toolId: 'image-resize', reason: 'Resizing image' },
  { test: /\b(linkedin|twitter|x|instagram|facebook).*(image|photo|cover|profile)/i, toolId: 'image-resize', reason: 'Resizing image for social platform' },
  { test: /\b(compress|shrink|reduce).*(image|photo|jpg|png)/i, toolId: 'image-compress', reason: 'Compressing image' },
  { test: /\b(crop|trim).*(image|photo)/i, toolId: 'image-crop', reason: 'Cropping image' },
  { test: /\b(convert|change format).*(webp|png|jpg|jpeg|avif)/i, toolId: 'image-convert', reason: 'Converting image format' },
  { test: /\b(remove|delete|strip).*(background|bg)/i, toolId: 'background-remover', reason: 'Background removal' },
  { test: /\b(gif|animation).*(image|frame|make|create)/i, toolId: 'gif-maker', reason: 'GIF animation' },
  { test: /\b(watermark|stamp).*(image|photo)/i, toolId: 'image-watermark', reason: 'Adding watermark' },
  { test: /\b(annotate|markup|arrow|highlight).*(image|screenshot)/i, toolId: 'image-annotator', reason: 'Annotating image' },
  { test: /\b(upscale|enlarge|enhance|super.*res)/i, toolId: 'image-upscale', reason: 'Upscaling image' },
  { test: /\b(rename|batch|bulk).*(image|file|photo)/i, toolId: 'image-bulk-rename', reason: 'Bulk renaming images' },
  { test: /\b(exif|metadata|gps).*(image|photo|strip|scrub)/i, toolId: 'exif-scrub', reason: 'EXIF metadata cleanup' },
  { test: /\b(ocr|extract.*text|read.*image|image.*text)/i, toolId: 'ocr', reason: 'OCR — image to text' },

  // Data / spreadsheets
  { test: /\b(remove|delete).*duplicate.*(csv|spreadsheet|rows)/i, toolId: 'csv-dedupe', reason: 'Removing duplicates from CSV' },
  { test: /\b(clean|tidy).*(csv|data)/i, toolId: 'csv-clean', reason: 'Cleaning CSV' },
  { test: /\bcsv.*(json)/i, toolId: 'csv-to-json', reason: 'Converting CSV to JSON' },
  { test: /\bjson.*(csv)/i, toolId: 'csv-to-json', reason: 'Converting JSON to CSV' },
  { test: /\b(excel|xlsx|spreadsheet|sheet)/i, toolId: 'xlsx-convert', reason: 'Excel conversion' },
  { test: /\b(pivot|crosstab|cross.tab)/i, toolId: 'csv-pivot', reason: 'Pivot table' },
  { test: /\b(checksum|verify.*hash|file.*hash|sha.*file)/i, toolId: 'file-checksum', reason: 'File checksum' },
  { test: /\b(zip|archive|extract.*zip|pack|unpack|7z|rar)/i, toolId: 'zip-tool', reason: 'ZIP archive' },
  { test: /\b(diff|compare).*(spreadsheet|excel|csv|sheet)/i, toolId: 'spreadsheet-diff', reason: 'Spreadsheet diff' },
  { test: /\b(chart|graph|visuali[sz]e|bar.chart|line.chart|pie.chart)/i, toolId: 'quick-chart', reason: 'Quick chart' },
  { test: /\b(duplicate.*file|find.*duplicate|same.*file)/i, toolId: 'duplicate-finder', reason: 'Find duplicate files' },
  { test: /\b(extract|pull).*(table|tabular)/i, toolId: 'csv-pivot', reason: 'Tabular extraction' },

  // Text / developer
  { test: /\b(format|pretty|beautify).*(json)/i, toolId: 'json-format', reason: 'Formatting JSON' },
  { test: /\b(minify|compact).*(json)/i, toolId: 'json-format', reason: 'Minifying JSON' },
  { test: /\b(base64|b64).*(encode|decode)?/i, toolId: 'base64', reason: 'Base64 encoding' },
  { test: /\b(url|uri).*(encode|decode)/i, toolId: 'url-encode', reason: 'URL encoding' },
  { test: /\b(regex|regular expression).*(test|match)/i, toolId: 'regex-tester', reason: 'Testing a regex' },
  { test: /\b(diff|compare).*(text|string|file)/i, toolId: 'text-diff', reason: 'Diffing text' },
  { test: /\b(case|uppercase|lowercase|titlecase|camel|snake|kebab)/i, toolId: 'case-converter', reason: 'Changing case' },
  { test: /\b(word|character).*(count)/i, toolId: 'word-counter', reason: 'Counting words' },
  { test: /\b(jwt|json web token)/i, toolId: 'jwt-decode', reason: 'Decoding a JWT' },
  { test: /\b(hash|sha-?\d+|md5)/i, toolId: 'hash-generator', reason: 'Hashing text' },
  { test: /\b(jsonpath|json.path|jq)/i, toolId: 'jsonpath', reason: 'JSONPath query' },
  { test: /\b(cron|schedule|crontab)/i, toolId: 'cron-builder', reason: 'Cron expression' },
  { test: /\b(svg).*(optim|minif|clean|compress)/i, toolId: 'svg-optimizer', reason: 'SVG optimization' },
  { test: /\b(api|rest|postman|http).*(test|request|call)/i, toolId: 'api-tester', reason: 'API request testing' },
  { test: /\b(curl|fetch).*(test|request)/i, toolId: 'api-tester', reason: 'API testing' },

  // Color
  { test: /\b(palette|color\s*scheme|brand colors?)/i, toolId: 'palette-generator', reason: 'Generating a palette' },
  { test: /\b(contrast|wcag|accessibility).*(check|ratio)/i, toolId: 'contrast-checker', reason: 'Checking contrast ratio' },
  { test: /\b(gradient|gradiant)/i, toolId: 'gradient-builder', reason: 'Building a gradient' },
  { test: /\b(color|colour).*(picker|extract)/i, toolId: 'color-picker', reason: 'Picking a color' },

  // Calculators
  { test: /\b(gst|sales tax|vat)/i, toolId: 'tax-calculator', reason: 'GST / tax math' },
  { test: /\b(income tax|tax slab|salary tax)/i, toolId: 'tax-calculator', reason: 'Income tax estimate' },
  { test: /\b(roi|return on investment|sip|cagr|invest)/i, toolId: 'roi-calculator', reason: 'ROI / investment math' },
  { test: /\b(unit|metric|imperial).*(convert|conversion)/i, toolId: 'unit-converter', reason: 'Converting units' },
  { test: /\b(timezone|time\s*zone|tz)/i, toolId: 'timezone-converter', reason: 'Converting timezones' },
  { test: /\b(currency|usd|inr|eur|gbp).*(convert|exchange)?/i, toolId: 'currency-converter', reason: 'Converting currency' },
  { test: /\b(emi|loan|mortgage)/i, toolId: 'emi-calculator', reason: 'Calculating EMI' },
  { test: /\b(percent|%)/i, toolId: 'percentage-calculator', reason: 'Calculating percentage' },
  { test: /\b(aspect|ratio).*(image|video|screen)?/i, toolId: 'aspect-ratio', reason: 'Aspect ratio math' },
  { test: /\b(date|days).*(between|difference|add|subtract)/i, toolId: 'date-calculator', reason: 'Date math' },
  { test: /\b(margin|markup|profit|roi)/i, toolId: 'margin-calculator', reason: 'Margin / ROI math' },

  // Tiny
  { test: /\bbarcode\b/i, toolId: 'barcode-generator', reason: 'Generating a barcode' },
  { test: /\b(qr|qr\s?code)/i, toolId: 'qr-generator', reason: 'Generating a QR code' },
  { test: /\b(password|pwd|passphrase).*(generate|create)/i, toolId: 'password-generator', reason: 'Generating a password' },
  { test: /\b(uuid|guid)/i, toolId: 'uuid-generator', reason: 'Generating UUIDs' },
  { test: /\b(lorem|ipsum|placeholder text)/i, toolId: 'lorem-ipsum', reason: 'Generating filler text' },
  { test: /\b(mock|fake|sample|seed).*(data|users|json)/i, toolId: 'mock-data', reason: 'Generating mock data' },
  { test: /\b(timer|stopwatch|pomodoro)/i, toolId: 'timer-stopwatch', reason: 'Timer / stopwatch' },
  { test: /\b(sticky|note|notes)/i, toolId: 'sticky-notes', reason: 'Sticky notes' },

  // Security
  { test: /\b(encrypt|decrypt|aes|encryption)/i, toolId: 'aes-encrypt', reason: 'AES encryption' },
  { test: /\b(metadata|exif).*(remove|strip|scrub)/i, toolId: 'metadata-scrub', reason: 'Stripping metadata' },
  { test: /\b(secret|token).*(generate|secure)/i, toolId: 'secret-generator', reason: 'Generating a secret' },

  // Writing
  { test: /\b(markdown|md).*(preview|render)?/i, toolId: 'markdown-preview', reason: 'Markdown preview' },
  { test: /\b(summari[sz]e|tldr|tl;dr)/i, toolId: 'summarizer', reason: 'Summarizing text' },
  { test: /\b(speech.to.text|dictat|voice.to.text|transcribe)/i, toolId: 'speech-to-text', reason: 'Speech to text' },
  { test: /\b(text.to.speech|tts|read.aloud|speak)/i, toolId: 'text-to-speech', reason: 'Text to speech' },
  { test: /\b(reader|reading.mode|distraction.free|article.view)/i, toolId: 'reading-mode', reason: 'Reading mode' },

  // Workplace
  { test: /\b(invoice|bill).*(generate|create)?/i, toolId: 'invoice-generator', reason: 'Generating an invoice' },
  { test: /\b(receipt|expense)/i, toolId: 'expense-tracker', reason: 'Tracking expenses' },
  { test: /\b(text.to.signature|typed.signature|cursive.name|autograph)/i, toolId: 'text-to-signature', reason: 'Generate signature from typed name' },
  { test: /\bsignature.*(font|cursive|name|typed|generate)/i, toolId: 'text-to-signature', reason: 'Generate signature from typed name' },

  // Browser / media
  { test: /\b(record.*screen|screen.*record|screencast)/i, toolId: 'screen-recorder', reason: 'Screen recording' },
  { test: /\b(video|mp4|webm).*(convert|transcode|compress)/i, toolId: 'video-transcode', reason: 'Video transcoding' },
  { test: /\b(audio.*extract|mp3.*from|extract.*audio)/i, toolId: 'video-transcode', reason: 'Extract audio' },
  { test: /\b(youtube|tiktok|instagram).*(download|mp3|mp4|save)/i, toolId: 'media-downloader', reason: 'Download from social platform' },
  { test: /\b(download|save).*(youtube|tiktok|instagram|reel|short|video|tweet)/i, toolId: 'media-downloader', reason: 'Download social media' },
  { test: /\b(youtube|yt).*(audio|mp3|music)/i, toolId: 'media-downloader', reason: 'YouTube audio download' },
];

export interface IntentResult {
  toolId: string | null;
  reason: string;
  matches: Tool[];
}

export function routeIntent(input: string): IntentResult {
  const text = input.trim();
  if (!text) return { toolId: null, reason: '', matches: [] };

  for (const rule of INTENT_RULES) {
    if (rule.test.test(text)) {
      const tool = TOOLS.find((t) => t.id === rule.toolId);
      if (tool) {
        const matches = searchTools(text, 5);
        return { toolId: rule.toolId, reason: rule.reason, matches };
      }
    }
  }

  // Fall through to fuzzy
  const matches = searchTools(text, 5);
  return {
    toolId: matches[0]?.id ?? null,
    reason: matches[0] ? `Best match for "${text}"` : `No tool matched "${text}"`,
    matches,
  };
}

/* -------------------------------------------------------------------------- */
/*  File → tool routing                                                       */
/* -------------------------------------------------------------------------- */

export function suggestToolForFile(file: File): string | null {
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();

  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf-thumbnails';
  if (name.endsWith('.svg') || type === 'image/svg+xml') return 'svg-optimizer';
  if (type.startsWith('video/') || /\.(mp4|webm|mov|mkv|avi)$/.test(name)) return 'video-transcode';
  if (type.startsWith('audio/') || /\.(mp3|wav|m4a|ogg|flac)$/.test(name)) return 'video-transcode';
  if (type.startsWith('image/') || /\.(png|jpe?g|webp|gif|avif|heic)$/.test(name)) return 'image-resize';
  if (name.endsWith('.csv') || type === 'text/csv') return 'csv-clean';
  if (name.endsWith('.json') || type.includes('json')) return 'json-format';
  if (/\.(xlsx?|xls|tsv)$/.test(name)) return 'xlsx-convert';
  if (name.endsWith('.md') || name.endsWith('.markdown')) return 'markdown-preview';
  if (name.endsWith('.zip') || type === 'application/zip') return 'zip-tool';
  if (type.startsWith('text/') || /\.(txt|log)$/.test(name)) return 'text-diff';
  return null;
}
